import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { CompanyProvider } from './contexts/CompanyContext';
import { AuthProvider } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';
import LoginPage from './components/LoginPage';
import MainDashboard from './components/MainDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import PublicLandingPage from './components/PublicLandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Footer from './components/Footer';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// PCI-DSS Compliance: Remove any legacy credit card data from localStorage
if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('dispatcher_cc_details');
}

// Public routes render OUTSIDE all providers — no API calls, no auth, no possibility of redirects.
const PublicShell = ({ children }) => (
    <div className="app" style={{ display: 'flex', flexDirection: 'column', minHeight: '100svh' }}>
        <div style={{ flex: 1 }}>{children}</div>
    </div>
);

// Authenticated routes render INSIDE the provider stack.
const AuthenticatedApp = () => (
    <CompanyProvider>
        <AuthProvider>
            <SettingsProvider>
                <AlertProvider>
                    <GoogleMapsProvider>
                        <div className="app" style={{ display: 'flex', flexDirection: 'column', minHeight: '100svh' }}>
                            <div style={{ flex: 1 }}>
                                <SentryRoutes>
                                    {/* Login page */}
                                    <Route path="/login" element={<LoginPage />} />

                                    {/* Company-scoped dashboard */}
                                    <Route
                                        path="/:companyCode/dispatch/*"
                                        element={
                                            <ProtectedRoute>
                                                <MainDashboard />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Legacy redirects */}
                                    <Route path="/dispatch/*" element={<Navigate to="/login" replace />} />
                                    <Route path="/dashboard/*" element={<Navigate to="/login" replace />} />

                                    {/* Any unknown auth-shell route goes to login */}
                                    <Route path="*" element={<Navigate to="/login" replace />} />
                                </SentryRoutes>
                            </div>
                            <Footer />
                        </div>
                    </GoogleMapsProvider>
                </AlertProvider>
            </SettingsProvider>
        </AuthProvider>
    </CompanyProvider>
);

function App() {
    return (
        <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
            <Router>
                <SentryRoutes>
                    {/* Public pages — completely outside any provider */}
                    <Route path="/" element={<PublicShell><PublicLandingPage /></PublicShell>} />
                    <Route path="/privacy" element={<PublicShell><PrivacyPolicy /></PublicShell>} />
                    <Route path="/terms" element={<PublicShell><TermsOfService /></PublicShell>} />

                    {/* Everything else lives inside the authenticated provider stack */}
                    <Route path="/*" element={<AuthenticatedApp />} />
                </SentryRoutes>
            </Router>
        </Sentry.ErrorBoundary>
    );
}

export default App;
